import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import {
  FileText, DollarSign, Users, ArrowLeft, Plus, Download, Eye,
  Calculator, Calendar, CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const PayrollPayslipsPage = () => {
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerate, setShowGenerate] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: "",
    month: new Date().toISOString().slice(0, 7),
    overtime_hours: "0",
    bonuses: "0",
    deductions: "0"
  });
  const navigate = useNavigate();
  const token = localStorage.getItem("cdace_token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [payslipsRes, employeesRes] = await Promise.all([
          axios.get(`${API}/admin/payroll/payslips`, { headers }),
          axios.get(`${API}/admin/employees`, { headers })
        ]);
        setPayslips(payslipsRes.data.payslips || []);
        setEmployees(employeesRes.data.employees || []);
      } catch (error) {
        console.error("Error:", error);
        if (error.response?.status === 401) navigate("/admin-login");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, navigate]);

  const handleGenerate = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/payroll/payslips/generate`, {
        employee_id: formData.employee_id,
        month: formData.month,
        overtime_hours: parseFloat(formData.overtime_hours || 0),
        bonuses: parseFloat(formData.bonuses || 0),
        deductions: parseFloat(formData.deductions || 0)
      }, { headers });
      toast.success("Payslip generated");
      setShowGenerate(false);
      // Refresh
      const payslipsRes = await axios.get(`${API}/admin/payroll/payslips`, { headers });
      setPayslips(payslipsRes.data.payslips || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate payslip");
    }
  };

  const formatCurrency = (val) => {
    if (!val) return "$0";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(val);
  };

  const totalNetPay = payslips.reduce((sum, p) => sum + (p.net_pay || 0), 0);
  const totalTax = payslips.reduce((sum, p) => sum + (p.tax_deduction || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1c2d] flex items-center justify-center">
        <div className="text-[#c9a227] text-xl">Loading Payslips...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b1c2d] p-6" data-testid="payroll-payslips-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/admin" className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Payslips
            </h1>
            <p className="text-slate-400 text-sm">Generate and manage employee payslips</p>
          </div>
        </div>
        <Button onClick={() => setShowGenerate(true)} className="bg-[#c9a227] text-[#0b1c2d] hover:bg-[#c9a227]/90">
          <Plus className="w-4 h-4 mr-2" /> Generate Payslip
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="glass-card">
          <CardContent className="p-4">
            <FileText className="w-6 h-6 text-[#c9a227] mb-2" />
            <p className="text-2xl font-bold text-[#c9a227]">{payslips.length}</p>
            <p className="text-xs text-slate-400">Total Payslips</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <DollarSign className="w-6 h-6 text-green-400 mb-2" />
            <p className="text-2xl font-bold text-green-400">{formatCurrency(totalNetPay)}</p>
            <p className="text-xs text-slate-400">Total Net Pay</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Calculator className="w-6 h-6 text-red-400 mb-2" />
            <p className="text-2xl font-bold text-red-400">{formatCurrency(totalTax)}</p>
            <p className="text-xs text-slate-400">Total Tax Withheld</p>
          </CardContent>
        </Card>
        <Card className="glass-card">
          <CardContent className="p-4">
            <Calendar className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-2xl font-bold text-blue-400">{new Date().toLocaleString('default', { month: 'short', year: 'numeric' })}</p>
            <p className="text-xs text-slate-400">Current Period</p>
          </CardContent>
        </Card>
      </div>

      {/* Generate Form Modal */}
      {showGenerate && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="glass-card w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-white">Generate Payslip</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleGenerate} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400">Employee</label>
                  <select
                    className="w-full mt-1 bg-slate-900 border border-[#c9a227]/20 rounded-sm p-2 text-white"
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-slate-400">Month</label>
                  <Input
                    type="month"
                    value={formData.month}
                    onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                    className="input-sovereign"
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm text-slate-400">OT Hours</label>
                    <Input
                      type="number"
                      value={formData.overtime_hours}
                      onChange={(e) => setFormData({ ...formData, overtime_hours: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Bonuses ($)</label>
                    <Input
                      type="number"
                      value={formData.bonuses}
                      onChange={(e) => setFormData({ ...formData, bonuses: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-400">Deductions ($)</label>
                    <Input
                      type="number"
                      value={formData.deductions}
                      onChange={(e) => setFormData({ ...formData, deductions: e.target.value })}
                      className="input-sovereign"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="bg-[#c9a227] text-[#0b1c2d]">Generate</Button>
                  <Button type="button" variant="outline" onClick={() => setShowGenerate(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payslip Detail Modal */}
      {selectedPayslip && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <Card className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white">Payslip Details</CardTitle>
              <Button variant="ghost" onClick={() => setSelectedPayslip(null)}>×</Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-start border-b border-slate-700 pb-4">
                <div>
                  <p className="text-xl font-bold text-white">{selectedPayslip.employee_name}</p>
                  <p className="text-slate-400">{selectedPayslip.month}</p>
                </div>
                <Badge className="bg-green-500/20 text-green-400">Generated</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <h4 className="text-[#c9a227] font-semibold">Earnings</h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Basic Salary</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.basic_salary)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Housing Allowance</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.housing_allowance)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Transport Allowance</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.transport_allowance)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Medical Allowance</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.medical_allowance)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Overtime ({selectedPayslip.overtime_hours}h)</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.overtime_pay)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Bonuses</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.bonuses)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t border-slate-700 pt-2">
                    <span className="text-[#c9a227]">Gross Earnings</span>
                    <span className="text-[#c9a227]">{formatCurrency(selectedPayslip.gross_earnings)}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="text-red-400 font-semibold">Deductions</h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Pension</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.pension_deduction)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">PAYE Tax</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.tax_deduction)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">UIF</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.uif_deduction)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Other Deductions</span>
                    <span className="text-white">{formatCurrency(selectedPayslip.other_deductions)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t border-slate-700 pt-2">
                    <span className="text-red-400">Total Deductions</span>
                    <span className="text-red-400">{formatCurrency(selectedPayslip.total_deductions)}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#c9a227]/10 border border-[#c9a227]/30 rounded-sm p-4 text-center">
                <p className="text-slate-400 text-sm">Net Pay</p>
                <p className="text-3xl font-bold text-[#c9a227]">{formatCurrency(selectedPayslip.net_pay)}</p>
              </div>
              
              <div className="flex gap-3 justify-end">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payslips Table */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Recent Payslips</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Month</th>
                  <th>Gross</th>
                  <th>Deductions</th>
                  <th>Net Pay</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payslips.map((payslip) => (
                  <tr key={payslip.id}>
                    <td className="text-white font-medium">{payslip.employee_name}</td>
                    <td className="text-slate-300">{payslip.month}</td>
                    <td className="text-blue-400">{formatCurrency(payslip.gross_earnings)}</td>
                    <td className="text-red-400">{formatCurrency(payslip.total_deductions)}</td>
                    <td className="text-[#c9a227] font-semibold">{formatCurrency(payslip.net_pay)}</td>
                    <td>
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="w-3 h-3 mr-1" /> {payslip.status}
                      </Badge>
                    </td>
                    <td>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => setSelectedPayslip(payslip)}
                        className="h-8 px-2 text-[#c9a227]"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PayrollPayslipsPage;
